import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DeleteAthleteDialog } from "@/components/DeleteAthleteDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Pencil, Trash2, ExternalLink, Download, Users, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import ImportAthletesV2Dialog from "@/components/ImportAthletesV2Dialog";
import DuplicatesDialog from "@/components/DuplicatesDialog";
import GoleeImportDialog from "@/components/GoleeImportDialog";
import BulkAthleteActionsBar from "@/components/BulkAthleteActionsBar";
import CategoryBadges from "@/components/CategoryBadges";
import WeaponSummary from "@/components/WeaponSummary";
import ExcelHeaderFilter from "@/components/ExcelHeaderFilter";
import ActivityBadge from "@/components/ActivityBadge";
import { useSeason } from "@/context/SeasonContext";
import { useAuth } from "@/context/AuthContext";
import { useSeasonCategories } from "@/hooks/useSeasonCategories";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

const STATUS_STYLES = {
  disponibile: "bg-emerald-50 text-emerald-700",
  allenamento_ridotto: "bg-amber-50 text-amber-700",
  infortunato: "bg-red-50 text-red-700",
  return_to_play: "bg-orange-50 text-orange-700",
  assente: "bg-zinc-100 text-zinc-600",
};
const STATUS_ORDER = { disponibile: 0, allenamento_ridotto: 1, return_to_play: 2, infortunato: 3, assente: 4 };
const STATUS_LABEL = {
  disponibile: "Disponibile",
  allenamento_ridotto: "Allenamento ridotto",
  infortunato: "Infortunato",
  return_to_play: "Return to play",
  assente: "Assente",
};
const ACTIVITY_ORDER = { agonista: 0, non_agonista: 1, __unset: 2 };
const ACTIVITY_LABEL = { agonista: "Agonista", non_agonista: "Non agonista", __unset: "Non impostato" };

const emptyForm = { first_name: "", last_name: "", weapon: null, monitoring_level: "standard", status: "disponibile", birth_date: "", gender: null, years_fencing: 0, technical_level: "principiante", active: true };

const EMPTY_SET = () => new Set();

export default function Athletes() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");

  // Excel-like filters: each column keeps a Set of selected values (empty = no filter)
  const [colFilters, setColFilters] = useState({
    category: EMPTY_SET(),
    weapon: EMPTY_SET(),
    activity: EMPTY_SET(),
    group: EMPTY_SET(),
    status: EMPTY_SET(),
  });
  // Sort: one column at a time (Excel-like). {col: 'athlete'|'category'|..., dir: 'asc'|'desc'}
  const [sort, setSort] = useState({ col: "athlete", dir: "asc" });
  const [viewFilter, setViewFilter] = useState("active");
  const [selected, setSelected] = useState(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [goleeOpen, setGoleeOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const nav = useNavigate();
  const { selectedSeasonId } = useSeason();
  const { user } = useAuth();
  const { categories, catMap, filterByCategory } = useSeasonCategories(selectedSeasonId);
  const [weaponsMap, setWeaponsMap] = useState({});

  const load = async () => {
    const url = selectedSeasonId ? `/athletes-with-categories?season_id=${selectedSeasonId}` : `/athletes-with-categories`;
    const [awc, g, aww] = await Promise.all([api.get(url), api.get("/groups"), api.get("/athletes-with-weapons")]);
    setItems(awc.data?.athletes || []);
    setGroups(g.data);
    const wm = {};
    (aww.data || []).forEach((a) => { wm[a.id] = a.weapons || []; });
    setWeaponsMap(wm);
  };
  useEffect(() => { load(); }, [selectedSeasonId]);

  const openNew = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...emptyForm, ...a }); setEditingId(a.id); setOpen(true); };

  const save = async () => {
    try {
      let created = null;
      if (editingId) {
        await api.put(`/athletes/${editingId}`, { ...form, id: editingId });
        toast.success("Atleta aggiornato");
        created = { id: editingId };
      } else {
        const { data } = await api.post("/athletes", form);
        toast.success("Atleta creato");
        created = data;
      }
      if (created?.id) { try { await api.post(`/athletes/${created.id}/recompute-category`, {}); } catch (_) {} }
      setOpen(false); setForm(emptyForm); setEditingId(null); load();
    } catch { toast.error("Errore"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/athletes/${confirmDelete.id}`);
      toast.success("Atleta eliminato");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || "Errore"); }
  };

  const exportRoster = () => {
    const params = new URLSearchParams();
    // Passa i filtri backend-supportati (arma/gruppo/attività) al server; il resto
    // lo calcoliamo lato client dopo il download del file.
    const one = (s) => (s && s.size === 1 ? Array.from(s)[0] : null);
    const w = one(colFilters.weapon); if (w) params.set("weapon", w);
    const g = one(colFilters.group); if (g) params.set("group_id", g);
    const at = one(colFilters.activity);
    if (at) params.set("activity_type", at === "__unset" ? "unset" : at);
    const st = one(colFilters.status); if (st) params.set("status", st);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/athletes-roster?${params.toString()}`;
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("err"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `roster_atleti.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Roster scaricato");
      })
      .catch(() => toast.error("Errore export"));
  };

  // ---------- Roster derivato (view filter + search + col filters) ----------
  const activeAthletes = useMemo(() => {
    return items.filter((a) => {
      const arch = !!a.archived;
      if (viewFilter === "active" && arch) return false;
      if (viewFilter === "archived" && !arch) return false;
      if (viewFilter === "incomplete" && (a.first_name && a.last_name && a.birth_date && a.gender)) return false;
      if (viewFilter === "no_birth" && a.birth_date) return false;
      if (viewFilter === "no_email" && a.email) return false;
      if (viewFilter === "no_phone" && a.phone) return false;
              if (viewFilter === "no_group" && effGroups(a).length) return false;
      if (viewFilter === "no_weapon" && (weaponsMap[a.id] || []).some((w) => w.status === "attiva" || w.status === "secondaria")) return false;
      return true;
    });
  }, [items, viewFilter, weaponsMap]);

  // Weapon primaria da athlete_weapons (source-of-truth). Nessun fallback su athletes.weapon legacy.
  const weaponPrimary = (a) => {
    const ws = (weaponsMap[a.id] || []).filter((w) => w.status === "attiva" || w.status === "secondaria");
    const primary = ws.find((w) => w.is_primary) || ws[0];
    return primary?.weapon_code || null;
  };

  // Primary category per athlete (con sort_order per ordinamento sportivo)
  const catByCode = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.code] = c; });
    return m;
  }, [categories]);
  const primaryCategoryCode = (a) => {
    const arr = catMap[a.id] || [];
    const prim = arr.find((c) => c.is_primary) || arr[0];
    return prim?.category_code || null;
  };
  const categorySortValue = (a) => {
    const code = primaryCategoryCode(a);
    if (!code) return 9999;
    const c = catByCode[code];
    return c?.sort_order ?? 9999;
  };

  // Valori distinti per header filtro (calcolati da activeAthletes)
  const distinctValues = useMemo(() => {
    const cats = new Set();
    const weapons = new Set();
    const activities = new Set();
    const grps = new Set();
    const sts = new Set();
    for (const a of activeAthletes) {
      const cCode = primaryCategoryCode(a);
      cats.add(cCode); // può includere null
      weapons.add(weaponPrimary(a));
      activities.add(a.activity_type || "__unset");
      const _eg = effGroups(a); if (_eg.length) _eg.forEach((id) => grps.add(id)); else grps.add(null);
      sts.add(a.status);
    }
    // Categoria ordinata per sort_order
    const catsArr = Array.from(cats).sort((x, y) => {
      const sx = x ? (catByCode[x]?.sort_order ?? 9999) : 9999;
      const sy = y ? (catByCode[y]?.sort_order ?? 9999) : 9999;
      return sx - sy;
    });
    const weaponsArr = ["fioretto", "spada", "sciabola"].filter((w) => weapons.has(w));
    if (weapons.has(null)) weaponsArr.push(null);
    const activitiesArr = ["agonista", "non_agonista", "__unset"].filter((v) => activities.has(v));
    const groupsArr = Array.from(grps).sort((x, y) => {
      const gx = groups.find((g) => g.id === x)?.name || "";
      const gy = groups.find((g) => g.id === y)?.name || "";
      return gx.localeCompare(gy);
    });
    const statesArr = Object.keys(STATUS_ORDER).filter((s) => sts.has(s));
    return { cats: catsArr, weapons: weaponsArr, activities: activitiesArr, groups: groupsArr, states: statesArr };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAthletes, catByCode, groups]);

  // Filtro + search + sort
  const derived = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = activeAthletes.filter((a) => {
      if (term && !matchesSearch(a, term)) return false;
      if (colFilters.category.size > 0 && !colFilters.category.has(primaryCategoryCode(a))) return false;
      if (colFilters.weapon.size > 0 && !colFilters.weapon.has(weaponPrimary(a))) return false;
      if (colFilters.activity.size > 0 && !colFilters.activity.has(a.activity_type || "__unset")) return false;
      if (colFilters.group.size > 0) { const _eg = effGroups(a); const ok = _eg.length ? _eg.some((id) => colFilters.group.has(id)) : colFilters.group.has(null); if (!ok) return false; }
      if (colFilters.status.size > 0 && !colFilters.status.has(a.status)) return false;
      return true;
    });
    const dir = sort.dir === "desc" ? -1 : 1;
    list = list.slice().sort((x, y) => {
      let cmp = 0;
      switch (sort.col) {
        case "athlete": {
          const ax = `${x.last_name || ""} ${x.first_name || ""}`.toLowerCase();
          const ay = `${y.last_name || ""} ${y.first_name || ""}`.toLowerCase();
          cmp = ax.localeCompare(ay); break;
        }
        case "category": cmp = categorySortValue(x) - categorySortValue(y); break;
        case "weapon": cmp = String(weaponPrimary(x) || "zzz").localeCompare(String(weaponPrimary(y) || "zzz")); break;
        case "activity": {
          const ax = ACTIVITY_ORDER[x.activity_type || "__unset"];
          const ay = ACTIVITY_ORDER[y.activity_type || "__unset"];
          cmp = ax - ay; break;
        }
        case "group": {
          const gx = groups.find((g) => g.id === effGroups(x)[0])?.name || "zzz";
          const gy = groups.find((g) => g.id === effGroups(y)[0])?.name || "zzz";
          cmp = gx.localeCompare(gy); break;
        }
        case "status": cmp = (STATUS_ORDER[x.status] ?? 99) - (STATUS_ORDER[y.status] ?? 99); break;
        default: cmp = 0;
      }
      if (cmp === 0) cmp = `${x.last_name || ""}`.localeCompare(`${y.last_name || ""}`);
      return cmp * dir;
    });
    return list;
  }, [activeAthletes, q, colFilters, sort, catByCode, catMap, weaponsMap, groups]);

  // ---------- Selection ----------
  const filteredIds = derived.map((a) => a.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleAll = () => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => s.delete(id));
      else {
        if (filteredIds.length > 500 && !window.confirm(`Selezionare ${filteredIds.length} atleti? L'operazione potrebbe rallentare.`)) return prev;
        filteredIds.forEach((id) => s.add(id));
      }
      return s;
    });
  };
  const toggleOne = (id) => setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // ---------- Filter helpers ----------
  const setColFilter = (col, next) => setColFilters((prev) => ({ ...prev, [col]: next }));
  const anyFilterActive =
    q.trim().length > 0 ||
    Object.values(colFilters).some((s) => s.size > 0) ||
    viewFilter !== "active";
  const resetAllFilters = () => {
    setQ("");
    setColFilters({ category: EMPTY_SET(), weapon: EMPTY_SET(), activity: EMPTY_SET(), group: EMPTY_SET(), status: EMPTY_SET() });
    setViewFilter("active");
  };
  const setSortCol = (col, dir) => {
    if (dir === null) setSort({ col: "athlete", dir: "asc" });
    else setSort({ col, dir });
  };

  const activeFilterKeys = Object.entries(colFilters).filter(([, s]) => s.size > 0).map(([k]) => k);

  // ---------- Renderers per header ----------
  const renderCategoryValue = (code) => {
    if (!code) return <em className="text-zinc-400">— Non impostato</em>;
    const c = catByCode[code];
    return <span className="truncate">{c ? (c.male_name === c.female_name ? c.male_name : `${c.male_name} / ${c.female_name}`) : code}</span>;
  };
  const renderWeaponValue = (v) => v == null ? <em className="text-zinc-400">— Da definire</em> : <span className="capitalize">{v}</span>;
  const renderActivityValue = (v) => <span className="inline-flex"><ActivityBadge value={v === "__unset" ? null : v} /></span>;
  function effGroups(a) { return (a?.group_ids?.length ? a.group_ids : (a?.group_id ? [a.group_id] : [])); }
  const renderGroupValue = (v) => v == null ? <em className="text-zinc-400">— Nessun gruppo</em> : <span>{groups.find((g) => g.id === v)?.name || v}</span>;
  const renderStatusValue = (v) => <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLES[v] || ""}`}>{STATUS_LABEL[v] || v}</span>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Atleti</h1>
          <p className="text-zinc-500 mt-1">Anagrafica completa e profilo tecnico.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-roster-btn" variant="outline" className="h-12" onClick={exportRoster}><Download className="w-4 h-4 mr-2" />Esporta roster</Button>
          <Button data-testid="import-athletes-btn" variant="outline" className="h-12" onClick={() => setImportOpen(true)}><Upload className="w-4 h-4 mr-2" />Importa</Button>
          <Button data-testid="import-golee-btn" variant="outline" className="h-12" onClick={() => setGoleeOpen(true)}><Upload className="w-4 h-4 mr-2" />Import Golee</Button>
          {user?.role === "admin" && <DuplicatesDialog onDone={load} />}
          <ImportAthletesV2Dialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
          <GoleeImportDialog open={goleeOpen} onOpenChange={setGoleeOpen} onDone={load} cspGroups={groups} />
          <Button data-testid="new-athlete-btn" onClick={openNew} className="h-12 bg-black hover:bg-zinc-800 rounded-md"><Plus className="w-4 h-4 mr-2" />Nuovo atleta</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="athlete-search" placeholder="Cerca nome/cognome…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={viewFilter} onValueChange={setViewFilter}>
          <SelectTrigger data-testid="filter-view" className="w-48 h-11"><SelectValue placeholder="Vista" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid="view-active">Attivi</SelectItem>
            <SelectItem value="archived" data-testid="view-archived">Archiviati</SelectItem>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="incomplete">Schede incomplete</SelectItem>
            <SelectItem value="no_birth">Senza data di nascita</SelectItem>
            <SelectItem value="no_email">Senza email</SelectItem>
            <SelectItem value="no_phone">Senza telefono</SelectItem>
            <SelectItem value="no_group">Senza gruppo</SelectItem>
            <SelectItem value="no_weapon">Senza arma</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-3 text-sm text-zinc-500" data-testid="result-count">
          <span><strong className="font-mono text-black">{derived.length}</strong> / {items.length} atleti</span>
          {anyFilterActive && (
            <button
              data-testid="reset-filters"
              onClick={resetAllFilters}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider bg-zinc-100 hover:bg-zinc-200 px-2 py-1 rounded"
            >
              <RotateCcw className="w-3 h-3" />
              Reset filtri
            </button>
          )}
        </div>
      </div>

      {activeFilterKeys.length > 0 && (
        <div data-testid="active-filters" className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 flex-wrap">
          Filtri attivi:
          {activeFilterKeys.map((k) => (
            <span key={k} className="bg-black text-white rounded px-2 py-0.5 font-mono">
              {k} · {colFilters[k].size}
            </span>
          ))}
        </div>
      )}

      {selected.size > 0 && (
        <BulkAthleteActionsBar
          selectedIds={Array.from(selected)}
          isArchivedView={viewFilter === "archived"}
          onCleared={() => setSelected(new Set())}
          onDone={load}
        />
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="px-3 py-3 w-10"><input type="checkbox" data-testid="select-all" checked={allSelected} onChange={toggleAll} className="w-4 h-4" /></th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Atleta" testId="athlete" values={[]}
                  selected={new Set()} onSelectedChange={() => {}}
                  sortDir={sort.col === "athlete" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("athlete", d)}
                />
              </th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Categoria" testId="category" values={distinctValues.cats}
                  selected={colFilters.category} onSelectedChange={(s) => setColFilter("category", s)}
                  sortDir={sort.col === "category" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("category", d)}
                  renderValue={renderCategoryValue}
                />
              </th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Arma" testId="weapon" values={distinctValues.weapons}
                  selected={colFilters.weapon} onSelectedChange={(s) => setColFilter("weapon", s)}
                  sortDir={sort.col === "weapon" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("weapon", d)}
                  renderValue={renderWeaponValue}
                />
              </th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Attività" testId="activity" values={distinctValues.activities}
                  selected={colFilters.activity} onSelectedChange={(s) => setColFilter("activity", s)}
                  sortDir={sort.col === "activity" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("activity", d)}
                  renderValue={renderActivityValue}
                />
              </th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Gruppo" testId="group" values={distinctValues.groups}
                  selected={colFilters.group} onSelectedChange={(s) => setColFilter("group", s)}
                  sortDir={sort.col === "group" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("group", d)}
                  renderValue={renderGroupValue}
                />
              </th>
              <th className="px-5 py-3">
                <ExcelHeaderFilter
                  label="Stato" testId="status" values={distinctValues.states}
                  selected={colFilters.status} onSelectedChange={(s) => setColFilter("status", s)}
                  sortDir={sort.col === "status" ? sort.dir : null}
                  onSortChange={(d) => setSortCol("status", d)}
                  renderValue={renderStatusValue}
                />
              </th>
              <th className="px-5 py-3 text-right w-40">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {derived.map((a) => {
              const grpNames = effGroups(a).map((id) => groups.find((g) => g.id === id)?.name).filter(Boolean);
              return (
                <tr key={a.id} className={`border-b border-zinc-100 hover:bg-zinc-50 group ${a.archived ? "opacity-60" : ""} ${selected.has(a.id) ? "bg-blue-50" : ""}`}>
                  <td className="px-3 py-4"><input type="checkbox" data-testid={`select-${a.id}`} checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} className="w-4 h-4" /></td>
                  <td className="px-5 py-4">
                    <button data-testid={`athlete-open-${a.id}`} onClick={() => nav(`/atleti/${a.id}`)} className="font-medium hover:underline text-left flex items-center gap-2">
                      {a.photo_url ? (
                        <img src={a.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold">{initials(a)}</div>
                      )}
                      {displayName(a)}
                      {a.archived && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-zinc-200 text-zinc-600 rounded">archiviato</span>}
                    </button>
                  </td>
                  <td className="px-5 py-4"><CategoryBadges categories={catMap[a.id] || []} size="sm" /></td>
                  <td className="px-5 py-4"><WeaponSummary weapons={weaponsMap[a.id] || []} /></td>
                  <td className="px-5 py-4"><ActivityBadge value={a.activity_type} /></td>
                  <td className="px-5 py-4 text-sm text-zinc-600">
                    {grpNames.length === 0 ? <span className="text-zinc-400">—</span> : (
                      <div className="flex flex-wrap gap-1" data-testid={`athlete-groups-${a.id}`}>
                        {grpNames.map((n, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 bg-zinc-100 rounded">{n}</span>)}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-4"><span className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded ${STATUS_STYLES[a.status] || ""}`}>{STATUS_LABEL[a.status] || a.status?.replace("_", " ")}</span></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`athlete-view-${a.id}`} onClick={() => nav(`/atleti/${a.id}`)} title="Apri" className="p-2 rounded hover:bg-zinc-100 text-zinc-500 hover:text-black"><ExternalLink className="w-4 h-4" /></button>
                      <button data-testid={`athlete-edit-${a.id}`} onClick={() => openEdit(a)} title="Modifica" className="p-2 rounded hover:bg-zinc-100 text-zinc-500 hover:text-black"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`athlete-delete-${a.id}`} onClick={() => setConfirmDelete(a)} title="Elimina" className="p-2 rounded hover:bg-red-50 text-zinc-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {derived.length === 0 && (
              <tr>
                <td colSpan={8} className="px-5 py-12 text-center">
                  <Users className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <div className="text-sm text-zinc-600 mb-3">Nessun atleta trovato con i filtri correnti.</div>
                  {anyFilterActive
                    ? <Button size="sm" variant="outline" onClick={resetAllFilters} data-testid="empty-reset"><RotateCcw className="w-4 h-4 mr-2" />Reset filtri</Button>
                    : <Button size="sm" variant="outline" onClick={openNew} data-testid="empty-new-athlete"><Plus className="w-4 h-4 mr-2" />Aggiungi il primo atleta</Button>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifica atleta" : "Nuovo atleta"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input data-testid="athlete-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
              <div><Label>Cognome</Label><Input data-testid="athlete-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Arma</Label>
                <Select value={form.weapon || "__none"} onValueChange={(v) => setForm({ ...form, weapon: v === "__none" ? null : v })}>
                  <SelectTrigger data-testid="athlete-weapon"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Arma non impostata</SelectItem>
                    <SelectItem value="fioretto">Fioretto</SelectItem>
                    <SelectItem value="spada">Spada</SelectItem>
                    <SelectItem value="sciabola">Sciabola</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Attività</Label>
                <Select value={form.activity_type || "__unset"} onValueChange={(v) => setForm({ ...form, activity_type: v === "__unset" ? null : v })}>
                  <SelectTrigger data-testid="athlete-form-activity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agonista">Agonista</SelectItem>
                    <SelectItem value="non_agonista">Non agonista</SelectItem>
                    <SelectItem value="__unset">Non impostato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data di nascita</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} /></div>
              <div>
                <Label>Sesso</Label>
                <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="athlete-gender-new"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschile</SelectItem>
                    <SelectItem value="F">Femminile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Gruppi CSP</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="athlete-form-groups">
                {groups.map((g) => {
                  const cur = form.group_ids?.length ? form.group_ids : (form.group_id ? [form.group_id] : []);
                  const on = cur.includes(g.id);
                  return (
                    <button key={g.id} type="button" data-testid={`form-group-toggle-${g.id}`}
                      onClick={() => { const s = new Set(cur); on ? s.delete(g.id) : s.add(g.id); const ids = [...s]; setForm({ ...form, group_ids: ids, group_id: ids[0] || null }); }}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}>
                      {g.name}
                    </button>
                  );
                })}
                {groups.length === 0 && <span className="text-xs text-zinc-400">Nessun gruppo</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="athlete-save" onClick={save} className="bg-black hover:bg-zinc-800">{editingId ? "Aggiorna" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteAthleteDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        athleteId={confirmDelete?.id}
        label={confirmDelete ? `${confirmDelete.first_name} ${confirmDelete.last_name}` : ""}
        onConfirm={doDelete}
        onArchived={load}
      />
    </div>
  );
}
